import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { confirmAlert } from 'react-confirm-alert';

import API from '@components/api';
import BasicNavMenu from '@components/Navbars/Basic';
import Textbox from '@controls/textbox';
import { scrollTo, MSG_DIRECT_ADMIT } from '@modules/_misc';
import { useMounted } from '@modules/hooks';
import _svg from '@modules/svg';
import { globalActions } from '@reducers/global';
import { userActions } from '@reducers/user';
import MobileSidebar from '@views/Public/shared-content/mobile-sidebar';
import ShowNotifications from '@views/Public/shared-content/show-notifications';
import withRouter from '@wrappers/withRouter';

const LinkDirectAdmit = props => {
	const { navigate } = props,
		dispatch = useDispatch(),
		support_student_id = useSelector(state => state.user.support_student_id),
		campus_admin = useSelector(state => state.user.campus_admin),
		direct_admit = useSelector(state => state.user.direct_admit),
		prev_page = useSelector(state => state.global.prev_page),
		setDirectAdmit = obj => dispatch(userActions.setDirectAdmit(obj)),
		captureError = err => dispatch(globalActions.captureError(err)),
		[email, setEmail] = useState(''),
		[code, setCode] = useState(''),
		[working, setWorking] = useState(false),
		[showSuccess, setShowSuccess] = useState(false),
		[resent, setResent] = useState(false),
		[newAccount, setNewAccount] = useState(false),
		allowForcedNavRef = useRef(false),
		mounted = useMounted(),
		api = useMemo(() => new API('', support_student_id, campus_admin), [support_student_id, campus_admin]);

	useEffect(() => {
		scrollTo();

		if (prev_page === '/verify' && !!direct_admit?.email) {
			setNewAccount(true);
			api.sendDirectAdmitVerifyEmail(direct_admit.email).catch(ex => captureError(ex));
		}
	}, []);

	useEffect(() => {
		if (direct_admit.user_id) setShowSuccess(true);
	}, [direct_admit]);

	useEffect(() => {
		if (showSuccess) {
			allowForcedNavRef.current = true;
		} else if (allowForcedNavRef.current) {
			navigate('/my-account');
		}
	}, [showSuccess]);

	function onVerifyEmail() {
		setWorking(true);

		api.sendDirectAdmitVerifyEmail(email)
			.then(resp => {
				setWorking(false);

				if (resp.result === 'success') {
					setDirectAdmit({ email: email });
				} else if (mounted()) {
					confirmAlert({
						customUI: ({ onClose }) => {
							return (
								<div className='react-confirm-alert'>
									<div className='react-confirm-alert-body'>
										{MSG_DIRECT_ADMIT.send_email(resp.error, email)}

										<div className='react-confirm-alert-button-group'>
											<button onClick={onClose}>OK</button>
										</div>
									</div>
								</div>
							);
						}
					});
				}
			})
			.catch(ex => captureError(ex));
	}

	function onVerifyCode() {
		setWorking(true);

		api.verifyDirectAdmitEmail(direct_admit.email, code)
			.then(resp => {
				if (mounted()) {
					if (resp.result === 'success') {
						setWorking(false);
						setDirectAdmit(resp.record);
					} else {
						confirmAlert({
							customUI: ({ onClose }) => {
								return (
									<div className='react-confirm-alert'>
										<div className='react-confirm-alert-body'>
											{MSG_DIRECT_ADMIT.verify_record(resp.error, direct_admit.email)}

											<div className='react-confirm-alert-button-group'>
												<button
													disabled={working}
													onClick={() => {
														setWorking(true);
														setCode('');

														api.sendDirectAdmitVerifyEmail(direct_admit.email).then(
															resp => {
																if (mounted()) {
																	setWorking(false);
																	onClose();
																}
															}
														);
													}}>
													Send me a new code
												</button>
												<button disabled={working} onClick={onClose}>
													OK
												</button>
											</div>
										</div>
									</div>
								);
							}
						});
					}
				}
			})
			.catch(ex => captureError(ex));
	}

	function onResendCode() {
		try {
			setCode('');
			setResent(true);

			api.sendDirectAdmitVerifyEmail(direct_admit.email).catch(ex => captureError(ex));

			setTimeout(() => {
				setResent(false);
			}, 3000);
		} catch (ex) {
			captureError(ex);
		}
	}

	function renderEmailForm() {
		return (
			<>
				<h1 className='column-heading'>Let's find your Direct Admit offers</h1>

				<p className='last-p'>
					Enter your <strong>high school email address</strong> included on your Direct Admit letter to verify
					your identity and access your Direct Admit offers.
				</p>

				<Textbox
					value={email}
					onChange={val => setEmail(val)}
					name='email'
					label='Email'
					required={true}
					maxLength={70}
				/>

				<input
					type='button'
					onClick={() => onVerifyEmail()}
					disabled={working}
					className='btn-general primary'
					value='Verify'
				/>
			</>
		);
	}

	function renderCodeForm() {
		return (
			<>
				{newAccount ? (
					<>
						<h1 className='column-heading'>Looking Good!</h1>

						<p>
							Thanks! Your account has been verified and is ready to go. To access your Direct Admit
							admission offers, we need to verify your high school email too.
						</p>
						<p className='last-p'>
							We have sent a code to <strong>{direct_admit.email}</strong>.
						</p>
					</>
				) : (
					<>
						<h1>Let's find your Direct Admit offers</h1>

						<p className='last-p'>
							We've sent a code to <strong>{direct_admit.email}</strong>.
						</p>
					</>
				)}

				<Textbox
					value={code}
					onChange={val => setCode(val)}
					name='code'
					label='Code'
					required={true}
					maxLength={10}
					classVals={['floating-short']}
				/>

				<div className='btn-line-wrapper'>
					<input
						type='button'
						onClick={() => onVerifyCode()}
						disabled={resent}
						className='btn-general primary'
						value='Verify'
					/>
					<input
						type='button'
						onClick={() => onResendCode()}
						disabled={resent || working}
						className={`btn-general ${resent ? 'triggered' : ''}`}
						value={resent ? 'Resent!' : 'Resend Code'}
					/>
				</div>

				<p className='link-da-no-code'>Not receiving your code? Please give us a call at 800-442-6459.</p>
			</>
		);
	}

	function renderSuccess() {
		return (
			<>
				<h1 className='column-heading'>Email Verified</h1>

				{newAccount ? (
					<p>That&apos;s what we needed!</p>
				) : (
					<p>Great! Now you can access your Direct Admit offers.</p>
				)}

				<div className='success-img-wrapper'>
					<img alt='Success' src={_svg.InitInfoYes} />
				</div>

				<input
					type='button'
					className='btn-general btn-da-linked'
					onClick={() => navigate('/my-account')}
					value={"OK, Let's go!"}
				/>
			</>
		);
	}

	function renderContent() {
		if (direct_admit.user_id) return renderSuccess();

		if (direct_admit?.email) return renderCodeForm();

		return renderEmailForm();
	}

	return (
		<div className='body-2' role='main'>
			<MobileSidebar />

			<div className='two-column-view' role='navigation'>
				<BasicNavMenu />

				<div className='right-column'>
					<ShowNotifications />

					<div className='right-column-wrap direct-admit-form'>
						<div className='form-block w-form'>{renderContent()}</div>
					</div>
				</div>
			</div>
			<script
				src='https://d3e54v103j8qbb.cloudfront.net/js/jquery-3.4.1.min.220afd743d.js?site=5ea1ccd99c38e78f25db182e'
				type='text/javascript'
				integrity='sha256-CSXorXvZcTkaix6Yvo6HppcZGetbYMGWSFlBw8HfCJo='
				crossOrigin='anonymous'></script>
			<script src='@webflowJS/webflow.js' type='text/javascript'></script>
		</div>
	);
};

export default withRouter(LinkDirectAdmit);
